/**
 * @file ProtectedRoute.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Route protection component for the Shay Suite Admin application.
 *              This file provides authentication-based route protection that
 *              redirects unauthenticated users to the login page and implements
 *              role-based routing for different admin types.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Props interface for the ProtectedRoute component
 */
interface ProtectedRouteProps {
  children: React.ReactNode;  // Child components to be protected
}

/**
 * Protected Route Component
 * 
 * This component:
 * - Checks if the user is authenticated using the AuthContext
 * - Redirects unauthenticated users to the login page
 * - Implements role-based routing for different admin types
 * - Company admins are redirected to companysignup page
 * - Root admins are redirected to dashboard
 * - Renders child components only for authenticated users with proper access
 * 
 * @param {ProtectedRouteProps} props - Component props containing children to protect
 * @returns {JSX.Element} Either the protected children or a redirect to appropriate page
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isRootAdmin, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user is admin but not root admin (company admin), redirect to companysignup
  if (user?.role === 'admin' && !isRootAdmin) {
    // Allow access to company admin pages
    if (location.pathname === '/companysignup' || location.pathname === '/onboarding') {
      return <>{children}</>;
    }
    // Redirect to companysignup for other pages
    return <Navigate to="/companysignup" replace />;
  }

  // If user is root admin, allow access to all admin pages
  if (isRootAdmin) {
    return <>{children}</>;
  }

  // For other user types (customers, etc.), allow access
  return <>{children}</>;
};

export default ProtectedRoute; 